/**
 * Cart store — vanilla subscribe pattern + useSyncExternalStore hook.
 *
 * localStorage-backed in-memory store, source of truth for UI. Lines live in
 * window.localStorage under `nysg-cart-v1`. Cross-tab updates via the
 * `storage` event.
 *
 * HISTORY: through 2026-08 this also shadow-wrote every mutation to Wix's
 * currentCart (lib/wix-cart-sync.ts) so abandoned carts surfaced in Wix
 * admin and hosted checkout had real line items. Migrated off Wix onto PAG
 * Commerce on the `pag-commerce-checkout` branch — PAG Commerce checkout
 * sessions are created once, at the moment of checkout (lib/pag-commerce),
 * not synced line-by-line as the cart changes, so that whole shadow-write
 * layer is gone. The cart itself stays local-only until checkout.
 */

import { useSyncExternalStore } from "react";
import type { Product } from "@/app/data/products";

const STORAGE_KEY = "nysg-cart-v1";

export type CartLine = {
  /** Stable line id = `${productId}-${size}` (one line per size). */
  id: string;
  productId: string;
  /** Customer-facing name (full display name; falls back to brand+model+colorway). */
  name: string;
  /** Operator SKU / slug. */
  sku: string;
  size: number;
  priceCents: number;
  imageSrc?: string;
  qty: number;
  /** PAG Commerce product UUID carried by dynamically-fetched catalog
   *  products. */
  commerceProductId?: string;
  /** Resolved PAG Commerce variant UUID for the selected size — this is
   *  what checkout sends as `variant_id`. Absent means the line can't be
   *  checked out via Stripe and the WhatsApp fallback should be used. */
  variantId?: string;
};

export type CartState = {
  lines: CartLine[];
  subtotalCents: number;
  /** Whether the cart drawer is open. */
  drawerOpen: boolean;
};

// ─── Internal store ──────────────────────────────────────────────────────
let state: CartState = { lines: [], subtotalCents: 0, drawerOpen: false };
const listeners = new Set<() => void>();

function recomputeSubtotal(lines: CartLine[]): number {
  return lines.reduce((sum, l) => sum + l.priceCents * l.qty, 0);
}

function setState(next: Partial<CartState>) {
  state = { ...state, ...next };
  if (next.lines !== undefined) {
    state.subtotalCents = recomputeSubtotal(state.lines);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.lines));
      } catch {
        // quota or denied — swallow; we re-hydrate from memory next time
      }
    }
  }
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function getSnapshot(): CartState {
  return state;
}

// Server snapshot — identical empty shape so SSR renders consistently.
const EMPTY_STATE: CartState = { lines: [], subtotalCents: 0, drawerOpen: false };
function getServerSnapshot(): CartState {
  return EMPTY_STATE;
}

// ─── Hydration from localStorage + cross-tab sync ────────────────────────
let hydrated = false;
export function hydrateCart(): void {
  if (hydrated || typeof window === "undefined") return;
  hydrated = true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const lines = JSON.parse(raw) as CartLine[];
      if (Array.isArray(lines)) {
        state = { ...state, lines, subtotalCents: recomputeSubtotal(lines) };
        for (const fn of listeners) fn();
      }
    }
  } catch {
    // ignore malformed cart
  }

  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    try {
      const lines = e.newValue ? (JSON.parse(e.newValue) as CartLine[]) : [];
      state = { ...state, lines, subtotalCents: recomputeSubtotal(lines) };
      for (const fn of listeners) fn();
    } catch {
      // ignore
    }
  });
}

// ─── Public operations ───────────────────────────────────────────────────
function lineId(productId: string, size: number): string {
  return `${productId}-${size}`;
}

export function addLine(input: {
  product: Pick<
    Product,
    | "id"
    | "brand"
    | "model"
    | "colorway"
    | "sku"
    | "priceCents"
    | "imageSrc"
    | "commerceProductId"
    | "variantIdBySize"
  >;
  size: number;
}): void {
  const { product, size } = input;
  const id = lineId(product.id, size);
  const existing = state.lines.find((l) => l.id === id);

  const name = `${product.brand} ${product.model} — ${product.colorway}`;
  // PAG Commerce identifiers carried by dynamically-fetched catalog
  // products. Undefined for the static launch snapshot (app/data/products.ts),
  // which is no longer served live — see lib/catalog.ts.
  const commerceProductId = product.commerceProductId;
  const variantId = product.variantIdBySize?.[String(size)];

  if (existing) {
    setState({
      lines: state.lines.map((l) => (l.id === id ? { ...l, qty: l.qty + 1 } : l)),
    });
  } else {
    setState({
      lines: [
        ...state.lines,
        {
          id,
          productId: product.id,
          name,
          sku: product.sku,
          size,
          priceCents: product.priceCents,
          imageSrc: product.imageSrc,
          qty: 1,
          commerceProductId,
          variantId,
        },
      ],
    });
  }
}

export function removeLine(id: string): void {
  setState({ lines: state.lines.filter((l) => l.id !== id) });
}

export function updateQty(id: string, qty: number): void {
  if (qty <= 0) return removeLine(id);
  setState({
    lines: state.lines.map((l) => (l.id === id ? { ...l, qty } : l)),
  });
}

export function clearCart(): void {
  setState({ lines: [] });
}

export function openDrawer(): void {
  setState({ drawerOpen: true });
}

export function closeDrawer(): void {
  setState({ drawerOpen: false });
}

// ─── React hooks ──────────────────────────────────────────────────────────
export function useCart(): CartState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function useCartCount(): number {
  return useCart().lines.reduce((sum, l) => sum + l.qty, 0);
}
